import { useState } from "react";
import { motion } from "framer-motion";
import { LeadGate } from "./LeadGate";
import { ArrowUpRight, Download, FileText } from "lucide-react";
import { CASES, DOWNLOADS } from "../../data/content";
import { Eyebrow, Reveal } from "./Primitives";

const CaseCard = ({ c, i, big }) => (
  <Reveal delay={(i % 3) * 0.08} className={`h-full ${big ? "lg:col-span-3" : "lg:col-span-2"}`}>
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      data-testid={`case-card-${i}`}
      className="group flex h-full flex-col rounded-[28px] border border-ink/10 bg-white p-6 sm:p-8"
    >
      <div className="flex items-center justify-between">
        <span className="rounded-full bg-mist px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-ink/70">{c.tag}</span>
        <span className="font-mono text-[12px] text-ink/50">0{i + 1}</span>
      </div>
      <div className="mt-8 flex items-baseline gap-3">
        <span className={`font-display font-bold tracking-[-0.04em] text-ink ${big ? "text-6xl sm:text-7xl" : "text-5xl sm:text-6xl"}`}>{c.stat}</span>
      </div>
      <div className="mt-1 font-serif text-xl italic text-cobalt-700">{c.unit}</div>
      <p className="mt-5 text-[15px] leading-relaxed text-ink/70">{c.text}</p>
      {c.kpis && (
        <div className="mt-6 grid grid-cols-3 gap-2">
          {c.kpis.map(([v, l]) => (
            <div key={l} className="rounded-2xl border border-ink/10 px-3 py-2.5"><div className="font-display text-lg font-bold text-ink">{v}</div><div className="text-[12px] text-ink/60">{l}</div></div>
          ))}
        </div>
      )}
      <div className="spotlight mt-auto overflow-hidden rounded-2xl bg-mist pt-0">
        <div className="mt-6 h-36 overflow-hidden sm:h-44">
          <img src={c.img} alt={`${c.tag} campaign results`} loading="lazy" className="h-full w-full object-cover object-left-top mix-blend-multiply transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]" />
        </div>
      </div>
      {c.link ? (
        <a href={c.link} target="_blank" rel="noopener noreferrer" data-testid={`case-learn-more-${i}`} className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-ink underline-offset-4 hover:underline">
          Read the case study <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      ) : <span className="mt-5 text-[14px] text-ink/50">Verified campaign data</span>}
    </motion.article>
  </Reveal>
);

const DownloadCard = ({ d, i, onOpen }) => (
  <Reveal delay={i * 0.1} className="h-full">
    <article data-testid={`download-card-${d.id}`} className="group grid h-full overflow-hidden rounded-[28px] bg-ink text-paper sm:grid-cols-5">
      <div className="relative h-56 overflow-hidden sm:col-span-2 sm:h-full">
        <img src={d.cover} alt={`${d.title} case study preview`} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-left-top transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]" />
      </div>
      <div className="flex flex-col p-6 sm:col-span-3 sm:p-8">
        <span className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-cobalt-200"><FileText className="h-4 w-4" />{d.tag}</span>
        <h3 className="mt-4 font-display text-2xl font-bold leading-tight">{d.title}</h3>
        <div className="mt-6 grid grid-cols-3 gap-2">
          {d.kpis.map(([v, l]) => (
            <div key={l} className="rounded-2xl border border-paper/15 px-3 py-2.5"><div className="font-display text-lg font-bold">{v}</div><div className="text-[12px] text-paper/70">{l}</div></div>
          ))}
        </div>
        <button type="button" onClick={() => onOpen(d)} data-testid={`download-btn-${d.id}`} className="mt-8 inline-flex w-fit items-center gap-3 rounded-full bg-paper py-2 pl-6 pr-2 text-[15px] font-semibold text-ink transition-colors hover:bg-white sm:mt-auto">
          Download case study (PDF)
          <span className="grid h-9 w-9 place-items-center rounded-full bg-ink/10"><Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" /></span>
        </button>
      </div>
    </article>
  </Reveal>
);

const Downloads = () => {
  const [study, setStudy] = useState(null);
  return (
    <div id="case-studies" data-testid="download-case-studies" className="mt-20">
      <Reveal>
        <Eyebrow>Full case studies</Eyebrow>
        <h3 className="mt-4 font-display text-3xl font-bold tracking-[-0.02em] text-ink sm:text-4xl">Download the <span className="font-serif font-normal italic text-cobalt-700">complete story.</span></h3>
      </Reveal>
      <div className="mt-10 grid gap-5 lg:grid-cols-2">
        {DOWNLOADS.map((d, i) => <DownloadCard key={d.id} d={d} i={i} onOpen={setStudy} />)}
      </div>
      <LeadGate study={study} onClose={() => setStudy(null)} />
    </div>
  );
};

export const Results = () => (
  <section id="results" data-testid="results-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
    <div className="mb-14 grid gap-8 lg:grid-cols-12 lg:items-end">
      <Reveal className="lg:col-span-7">
        <Eyebrow>Straight to the point</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-ink sm:text-5xl lg:text-6xl">Results before <span className="font-serif font-normal italic text-cobalt-700">promises.</span></h2>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-5">
        <p className="text-base leading-relaxed text-ink/70 sm:text-lg">Real numbers from real engagements — across agencies, IT services, medical devices and AI. No vanity metrics, only pipeline that turned into revenue.</p>
      </Reveal>
    </div>
    <div className="grid gap-5 lg:grid-cols-6">
      {CASES.map((c, i) => <CaseCard key={i} c={c} i={i} big={i < 2} />)}
    </div>
    <Downloads />
  </section>
);
